import {defineMessages} from "react-intl";
import {FileMetadata} from "../common/model";
import {PaginationRequestParams} from "../common/util/pagination/PaginationUtil";
import {AccessRule, SpatialModel} from "../model";
import {GeneratedExtentType} from "../security/model";

export interface ImportedDataSearchFilter extends PaginationRequestParams {
  anyText?: string;
  type?: string;
  startDate?: string;
  endDate?: string;
  onlyTopLevelData?: boolean;
  folder?: string;
  datasets?: string;
  rsqlQuery?: string;
}

export const filtersEqual = (filterA: ImportedDataSearchFilter, filterB: ImportedDataSearchFilter) => {
  return filterA.anyText === filterB.anyText
         && filterA.type === filterB.type
         && filterA.startDate === filterB.startDate
         && filterA.endDate === filterB.endDate
         && filterA.folder === filterB.folder;
};

export interface ImportedData extends SpatialModel {
  creationTime?: string;
  filePath?: string;
  defaultStyleId?: string;
  fileMetadata?: FileMetadata;
  type: string;
  categories?: DataCategory[];
  dataType: ApolloCatalogDataType;
  temporalExtents?: string;
  customProperties?: any[];
}

export enum DataCategory {
  RASTER = "Raster",
  VECTOR = "Vector",
  MESH = "Mesh",
  POINT_CLOUD = "Point Cloud",
  PANORAMA = "Panorama",
  UNKNOWN = "Unknown",
}

export enum ApolloCatalogDataType {
  Folder,
  Image,
  Vector,
  PointCloud,
  Video,
  Generic,
}

export const APOLLO_CATALOG_ROOT_FOLDER = {
  id: '0',
  title: 'ROOT',
  path: `folder=`,
  parent: null
};

export interface ApolloCatalogDataParentRef {
  id: string;
  title: string;
}

export interface ApolloCatalogData extends ImportedData {
  parent: ApolloCatalogDataParentRef;
  path?: string;
}

export class FileStatus {
  static readonly SUCCESS = "Success";
  static readonly FAILED = "Failed";
  static readonly SKIPPED = "Skipped";
}

export const FILE_STATUS_MESSAGES = defineMessages({
  success: {
    id: "studio.data.file-status.success",
    defaultMessage: "Success",
  },
  failed: {
    id: "studio.data.file-status.failed",
    defaultMessage: "Failed",
  },
  skipped: {
    id: "studio.data.file-status.skipped",
    defaultMessage: "Skipped",
  },
});

export interface FileInfo {
  id: number;
  fileName: string;
  filePath: string;
  fileExtension: string;
  fileSize: number;
  directory: boolean;
  mimeType: string;
  firstScanTime: string;
  lastScanTime: string;
  status?: FileStatus;
  error?: string;
  dataId?: string;
  dataTitle?: string;
  type?: string;
  styleId?: string;
  styleTitle?: string;
  stackTrace?: string;
}

export interface Metadata {
  stopAtFirst: boolean,
  metadataCrawlers: string []
}

export interface CrawlJob {
  ignoreDeletedFiles?: boolean,
  forceRecrawlFiles?: boolean,
  jobSchedule?: string,
  decodeFailureResolution?: string,
  enqueueJob?: boolean,
  accessRuleTemplates?: AccessRule[]
}

export interface NewFileInfo {
  filePath: string,
  parent?: string,
  allowDownloads?: boolean,
  thumbnail?: boolean,
  pyramid?: boolean,
  accessRules?: AccessRule[],
  metadata?: Metadata,
  externalAttachments?: boolean,
  generatedExtent?: GeneratedExtentType
}

export interface NewDataRoot {
  rootPath: string,
  crawlJob?: CrawlJob,
  parent?: string,
  replicateDirectoryStructure?: boolean,
  allowDownloads?: boolean,
  thumbnail?: boolean,
  pyramid?: boolean,
  metadata?: Metadata,
  externalAttachments?: boolean,
  crawlTheseExtensionsOnly?: string[]
  generatedExtent?: GeneratedExtentType,
}

export interface CustomPropertyDetail {
  name: string,
  displayName: string,
  type: string,
  possibleValues?: string[],
}

export interface BatchProperties {
  patch?: BatchPatchProperties;
  propertiesPut?: any;
}

export interface BatchPatchProperties {
  title?: string;
  abstractText?: string;
  keywords?: string[];
  append?: BatchAppendProperties;
}

export interface BatchAppendProperties {
  abstractText?: string;
  keywords?: string[];
}

export enum AddType {
  DATA_ROOT = "DATA_ROOT",
  FILES = "FILES",
  DATABASE = "DATABASE",
  OGC = "OGC",
  UPLOAD = "UPLOAD",
  S3 = "S3"
}

export enum OgcServiceType {
  WMS = "WMS",
  WCS = "WCS",
  WFS = "WFS",
  WMTS = "WMTS"
}

export const OGC_FILE_EXTENSION = {
  [OgcServiceType.WMS]: "wms",
  [OgcServiceType.WCS]: "wcs",
  [OgcServiceType.WFS]: "wfs",
  [OgcServiceType.WMTS]: "wmts",
};

export interface OGCConnectionConfig {
  ogcConnectionName: string;
  ogcUrl: string;
  ogcServiceType: OgcServiceType;
  ogcLayer: string;
  ogcCoverage: string;
  ogcFeatureType: string;
}

export const EMPTY_OGC_CONNECTION = {
  ogcConnectionName: "",
  ogcUrl: "",
  ogcServiceType: OgcServiceType.WMS,
  ogcLayer: "",
  ogcCoverage: "",
  ogcFeatureType: "",
}

export const readOnlyCustomProperties = ["attachments", "crs_horizontal", "crs_vertical", "parent", "thumbnail",
                                         "dataType", "pyramids", "type", "bands", "unit", "pixelSizeX", "pixelSizeY",
                                         "width", "height"]

export const noDeleteButtonCustomProperties = ["abstractText", "Acquisition Date", "Allow downloads", "attachments",
                                               "bands", "creationTime", "crs_horizontal", "crs_vertical", "dataType",
                                               "extent", "height", "Image Dimensions (width,height)", "keywords",
                                               "owner", "parent", "Pixel Size (X,Y)", "pixelSizeX", "pixelSizeY",
                                               "pyramids", "statistics", "Temporal Extents", "thumbnail", "title",
                                               "type", "unit", "width"]
